import type { CheckGridAttention, CheckGridCellHistoryEntry, CheckGridDisplayState, CheckGridOverviewItem, OverviewLane, OverviewLaneKey } from "../types";

export const DISPLAY_STATE_LABEL: Record<CheckGridDisplayState, string> = {
  expected: "Expected",
  incomplete: "Incomplete",
  missed: "Missed",
  satisfied: "Complete",
  not_applicable: "Not scheduled"
};

export function cellStatusLabel(state: CheckGridDisplayState, attention?: CheckGridAttention | null): string {
  if (cellNeedsAttention(state, attention) && state !== "missed") return "Attention needed";
  return DISPLAY_STATE_LABEL[state];
}

type HistoryAction = { label: string; by: string | null; comment: string | null; state: CheckGridDisplayState | null };

/**
 * History rows are full snapshots, so each action is the difference from the row before it.
 * A later snapshot that still has evidence, and no other visible change, is a replacement.
 */
export function historyAction(entry: CheckGridCellHistoryEntry, prev: CheckGridCellHistoryEntry | undefined): HistoryAction | null {
  const notApplicable = entry.applicability === "not_applicable";
  const applicabilityChanged = prev ? entry.applicability !== prev.applicability : notApplicable;
  const checkedChanged = !notApplicable && (prev ? entry.checked !== prev.checked || applicabilityChanged : entry.checked);
  const commentChanged = (entry.comment ?? "") !== (prev?.comment ?? "");
  const commentLabel = !entry.comment ? "Removed comment" : prev?.comment ? "Edited comment" : "Commented";
  let evidenceLabel: string | null = null;
  if (!prev) {
    if (entry.has_evidence) evidenceLabel = "Evidence Attached";
  } else if (Boolean(entry.has_evidence) !== Boolean(prev.has_evidence)) {
    evidenceLabel = entry.has_evidence ? "Evidence Attached" : "Evidence Removed";
  } else if (entry.has_evidence && !checkedChanged && !applicabilityChanged && !commentChanged) {
    evidenceLabel = "Evidence Replaced";
  }

  let label: string | null = null;
  let state: CheckGridDisplayState | null = null;
  if (notApplicable && applicabilityChanged) {
    label = "Marked not applicable";
    state = "not_applicable";
  } else if (checkedChanged) {
    label = entry.checked ? "Marked complete" : applicabilityChanged ? "Marked applicable" : "Marked incomplete";
    state = entry.checked ? "satisfied" : "incomplete";
  }
  if (!label && !commentChanged && !evidenceLabel) return null;
  if (!label) label = commentChanged ? commentLabel : evidenceLabel;
  else if (commentChanged && !entry.comment) label = `${label} · removed comment`;
  if (!label) return null;
  if (evidenceLabel && label !== evidenceLabel) label = `${label} · ${evidenceLabel}`;

  const by = evidenceLabel && !state && !commentChanged ? null : state ? entry.checked_by ?? entry.comment_by : entry.comment_by ?? entry.checked_by;
  return { label, by, comment: commentChanged ? entry.comment : null, state };
}

export function satisfiedPercent(grid: CheckGridOverviewItem): number {
  const { complete, applicable } = satisfiedFraction(grid);
  if (applicable <= 0) return overviewSignal(grid) === "settled" ? 100 : 0;
  return Math.round((complete / applicable) * 100);
}

export function cellNeedsAttention(state: CheckGridDisplayState, attention?: CheckGridAttention | null): boolean {
  return state === "missed" || attention === "attention_needed";
}

/** Amber is reserved for attention; open checks stay muted so they don't compete with it. */
export function cellStateClass(state: CheckGridDisplayState, attention?: CheckGridAttention | null): string {
  switch (state) {
    case "satisfied":
      return "bg-success/85";
    case "incomplete":
      return cellNeedsAttention(state, attention) ? "bg-warning/90" : "bg-muted";
    case "missed":
      return "bg-destructive/25 text-destructive";
    case "expected":
      return "bg-transparent ring-1 ring-inset ring-muted-foreground/35";
    case "not_applicable":
      return "bg-muted/70";
    default:
      return "bg-muted";
  }
}

export function statusDotClass(state: CheckGridDisplayState, attention?: CheckGridAttention | null): string {
  if (state === "satisfied") return "bg-success";
  if (state === "missed") return "bg-destructive";
  if (state === "not_applicable") return "bg-muted-foreground/30";
  if (cellNeedsAttention(state, attention)) return "bg-warning";
  return "bg-muted-foreground/60";
}

export function cellCornerDotClass(state: CheckGridDisplayState, attention?: CheckGridAttention | null): string | null {
  if (state === "satisfied") {
    return "absolute top-[3px] right-[3px] size-[3px] rounded-full bg-background/70";
  }
  if (cellNeedsAttention(state, attention)) {
    return "absolute bottom-[2px] left-[2px] size-[3px] rounded-full bg-background/80";
  }
  return null;
}

export function signalMeta(signal: OverviewLaneKey): { dot: string; text: string; bar: string } {
  switch (signal) {
    case "attention":
      return { dot: "bg-warning", text: "text-warning", bar: "bg-warning" };
    case "settled":
      return { dot: "bg-success", text: "text-success", bar: "bg-success" };
    case "progress":
      return { dot: "bg-muted-foreground/50", text: "text-muted-foreground", bar: "bg-primary" };
    default:
      return { dot: "bg-muted-foreground/35", text: "text-muted-foreground", bar: "bg-muted-foreground/35" };
  }
}

export function countLabel(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function needAttention(count: number): string {
  return `need${count === 1 ? "s" : ""} attention`;
}

export function attentionHeadline(grid: CheckGridOverviewItem): string {
  const { attention_needed, missed } = grid.summary;
  return [attention_needed > 0 && `${countLabel(attention_needed, "item")} ${needAttention(attention_needed)}`, missed > 0 && countLabel(missed, "missed item")].filter(Boolean).join(" · ");
}

export function overviewSignal(grid: CheckGridOverviewItem): OverviewLaneKey {
  const { attention_needed, missed, unresolved } = grid.summary;
  if (attention_needed > 0 || missed > 0) return "attention";
  if (unresolved <= 0) return "settled";
  return "progress";
}

export function satisfiedFraction(grid: CheckGridOverviewItem): { complete: number; applicable: number } {
  const applicable =
    grid.summary.applicable > 0
      ? grid.summary.applicable
      : Math.max(grid.summary.subject_count * grid.summary.item_count, 0);
  const complete = Math.max(applicable - grid.summary.unresolved, 0);
  return { complete, applicable };
}

const LANE_ORDER: { key: OverviewLaneKey; title: string }[] = [
  { key: "attention", title: "Needs attention" },
  { key: "progress", title: "In progress" },
  { key: "settled", title: "Completed" }
];

export function buildOverviewLanes(grids: CheckGridOverviewItem[]): OverviewLane[] {
  return LANE_ORDER.map((lane) => ({
    ...lane,
    grids: grids.filter((g) => overviewSignal(g) === lane.key)
  })).filter((lane) => lane.grids.length > 0);
}

export function recurrenceLabel(recurrence: string): string {
  switch (recurrence) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "none":
      return "One-time";
    default:
      return recurrence;
  }
}
